import React from 'react';
import { PanelConfig } from '../../../core/types/panel.types';
import { usePanelStore } from '../../store/panelStore';
import { cn } from '../../utils/cn';
import * as Icons from 'lucide-react';

interface PanelHeaderProps {
  panel: PanelConfig;
  onStartDrag?: (e: React.MouseEvent) => void;
}

export const PanelHeader: React.FC<PanelHeaderProps> = ({ panel, onStartDrag }) => {
  const { closePanel, minimizePanel, floatPanel, dockPanel, restorePanel } =
    usePanelStore();

  const isFloating = panel.state === 'floating';
  const isMinimized = panel.state === 'minimized';

  // 🔴 Red button: Close panel (hide completely, reopen via Window menu)
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (panel.closeable) {
      closePanel(panel.id);
    }
  };

  // 🟡 Yellow button: Minimize to taskbar
  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (panel.minimizable) {
      minimizePanel(panel.id);
    }
  };

  // 🟢 Green button: Float/Dock toggle
  // - If docked → Float (detach and make draggable)
  // - If floating → Dock (return to original position)
  const handleFloatToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFloating) {
      dockPanel(panel.id, panel.position);
    } else {
      floatPanel(panel.id);
    }
  };

  // Get the icon component
  const IconComponent = (Icons as Record<string, React.FC<{ size?: number }>>)[panel.icon] || Icons.Box;

  return (
    <div
      className={cn(
        'flex items-center h-8 bg-zinc-800 border-b border-zinc-700 select-none',
        isFloating && 'cursor-move'
      )}
      onMouseDown={isFloating ? onStartDrag : undefined}
    >
      {/* Mac-style window controls (left side) */}
      <div className="flex items-center gap-1.5 px-2">
        {/* Close button (red) - Hide panel */}
        <button
          className={cn(
            'w-3 h-3 rounded-full transition-all group relative',
            panel.closeable
              ? 'bg-red-500 hover:bg-red-400'
              : 'bg-zinc-600 cursor-not-allowed'
          )}
          onClick={handleClose}
          title={panel.closeable ? 'Close (hide panel)' : 'Cannot close'}
        >
          {panel.closeable && (
            <Icons.X size={8} className="absolute inset-0 m-auto opacity-0 group-hover:opacity-100 text-red-900" />
          )}
        </button>

        {/* Minimize button (yellow) - Minimize to taskbar */}
        <button
          className={cn(
            'w-3 h-3 rounded-full transition-all group relative',
            panel.minimizable
              ? 'bg-yellow-500 hover:bg-yellow-400'
              : 'bg-zinc-600 cursor-not-allowed'
          )}
          onClick={handleMinimize}
          title={panel.minimizable ? 'Minimize to taskbar' : 'Cannot minimize'}
        >
          {panel.minimizable && (
            <Icons.Minus size={8} className="absolute inset-0 m-auto opacity-0 group-hover:opacity-100 text-yellow-900" />
          )}
        </button>

        {/* Float/Dock button (green) - Toggle floating */}
        <button
          className={cn(
            'w-3 h-3 rounded-full transition-all group relative',
            'bg-green-500 hover:bg-green-400'
          )}
          onClick={handleFloatToggle}
          title={isFloating ? 'Dock panel' : 'Float panel (drag to move)'}
        >
          {isFloating ? (
            <Icons.Pin size={8} className="absolute inset-0 m-auto opacity-0 group-hover:opacity-100 text-green-900" />
          ) : (
            <Icons.Move size={8} className="absolute inset-0 m-auto opacity-0 group-hover:opacity-100 text-green-900" />
          )}
        </button>
      </div>

      {/* Panel title with icon */}
      <div className="flex items-center gap-2 px-2 text-sm text-zinc-300">
        <IconComponent size={14} />
        <span className="font-medium">{panel.title}</span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Floating indicator */}
      {isFloating && (
        <div className="px-2 text-xs text-zinc-500">
          <Icons.Move size={12} />
        </div>
      )}
    </div>
  );
};
